import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Bell, CheckCircle, X as XIcon, WarningOctagon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationsContext";

/**
 * Bildirim çanı — sağ üstte açılan popover.
 *
 * Backend her 30 saniyede bir polled edilir. Okunmayan sayısı çanın üzerinde kırmızı
 * rozet olarak gösterilir. Ses çalınmaz — sessiz bildirim.
 * Bildirime tıklayınca ilgili sayfaya yönlendirir + otomatik okundu işaretler.
 */

const KIND_ROUTES = {
  retail: "/siparisler",
  pending: "/bekleyen",
  task: "/kesim-gorevleri",
  factory: "/fabrika-siparisleri",
  return: "/iadeler",
  monthly_report: "/istatistik#monthly-reports",
  weekly_summary: "/istatistik?period=last_week",
  kartela_request: "/kartela-talepleri",
};

export default function NotificationBell({ placement = "bottom" }) {
  const [open, setOpen] = useState(false);
  const data = useNotifications();
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openNotification = async (n) => {
    if (!n.read) data.markOne(n.id);
    const route = KIND_ROUTES[n.related_kind];
    if (route) {
      navigate(route);
    }
    setOpen(false);
  };

  const markOne = (nid, e) => {
    e.stopPropagation();
    data.markOne(nid);
  };
  const markAll = () => data.markAll();
  const delOne = (nid, e) => {
    e.stopPropagation();
    data.delOne(nid);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-muted transition-colors"
        aria-label="Bildirimler"
        data-testid="notification-bell"
      >
        <Bell size={18} weight={data.unread_count > 0 ? "fill" : "regular"} />
        {data.unread_count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div className={`absolute w-80 max-w-[92vw] max-h-[70vh] bg-card border border-border rounded-md shadow-xl z-[60] flex flex-col ${
          placement === "top"
            ? "bottom-0 left-full ml-2"
            : "right-0 mt-2"
        }`} data-testid="notification-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <div className="font-heading text-sm font-semibold">Bildirimler</div>
            {data.unread_count > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-primary hover:underline"
                data-testid="mark-all-read"
              >
                Tümünü okundu işaretle
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="text-center py-8 text-xs text-muted-foreground">
                Bildirim yok.
              </div>
            ) : (
              data.items.map((n) => (
                <div
                  key={n.id}
                  onClick={() => openNotification(n)}
                  className={`px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted/40 cursor-pointer transition-colors ${
                    n.read ? "" : "bg-primary/5"
                  }`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 shrink-0">
                      {n.kind === "warning" ? (
                        <WarningOctagon size={14} weight="fill" className="text-amber-600" />
                      ) : n.kind === "critical" ? (
                        <WarningOctagon size={14} weight="fill" className="text-destructive" />
                      ) : (
                        <Bell size={14} weight="fill" className="text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs leading-snug">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                        <span>
                          {new Date(n.created_at).toLocaleString("tr-TR", {
                            day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                        {KIND_ROUTES[n.related_kind] && (
                          <span className="text-primary">· Tıkla, siparişe git →</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {!n.read && (
                        <button
                          onClick={(e) => markOne(n.id, e)}
                          className="p-1 text-primary hover:bg-primary/10 rounded"
                          title="Okundu işaretle"
                        >
                          <CheckCircle size={12} />
                        </button>
                      )}
                      <button
                        onClick={(e) => delOne(n.id, e)}
                        className="p-1 text-muted-foreground hover:text-destructive rounded"
                        title="Sil"
                      >
                        <XIcon size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
